import * as React from 'react';
import { getMessagingProviderKeyConfigs, getPubSubMessagingProvider } from '@/redux/modules/config';
import { FilterOptions } from './hooks/filters';
import isEqual from 'react-fast-compare';
import useCatalogPubSub from './hooks/useCatalogPubSub';
import { useSelector } from 'react-redux';

type Props = {
    /** The catalog to subscribe for real-time events (onMessage, onStatus, onPresence etc) */
    catalogId: number;
    /** Applies a filter on message events to prevent additional work for specific use-cases */
    limitMessagesFilter?: FilterOptions;
    /** Ignore onStatus and onMessage events, useful when presence is the only data needed, default = false */
    presenceOnly?: boolean;
    /** Publish the current user's presence to pubsub provider, default = false */
    reportUserPresence?: boolean;
};

/**
 * Establishes connection to pub/sub services (ably+pubnub) to feed live auction data into redux, given a catalogId
 */
const PubnubCatalogContainer = ({
    catalogId,
    limitMessagesFilter,
    presenceOnly = false,
    reportUserPresence = false,
}: Props): null => {
    const { ably, pubnub } = useSelector(getMessagingProviderKeyConfigs, isEqual);
    const currentProvider = useSelector(getPubSubMessagingProvider, isEqual);

    const { connect: connectPubNub } = useCatalogPubSub({
        catalogId,
        limitMessagesFilter,
        presenceOnly,
        provider: 'pubnub',
        publishKey: pubnub.publishKey,
        reportUserPresence: reportUserPresence && currentProvider === 'pubnub',
        subscribeKey: pubnub.subscribeKey,
        subscribeToPresence: currentProvider === 'pubnub',
    });

    const { connect: connectAbly } = useCatalogPubSub({
        catalogId,
        limitMessagesFilter,
        presenceOnly,
        provider: 'ably',
        publishKey: ably.publishKey,
        reportUserPresence: reportUserPresence && currentProvider === 'ably',
        subscribeKey: ably.subscribeKey,
        subscribeToPresence: currentProvider === 'ably',
    });

    // Make initial connection to pub/sub providers
    React.useEffect(() => {
        try {
            connectPubNub();
        } catch (e) {
            console.error(`connecting to pubnub failed`, e);
        }

        try {
            connectAbly();
        } catch (e) {
            console.error(`connecting to ably failed`, e);
        }
    }, [connectAbly, connectPubNub]);

    return null;
};

export default PubnubCatalogContainer;
